import { DifficultyLevel } from '../interfaces/question.interface';
import { GridRound } from '../interfaces/treasure-grid.interface';

// Per-round grid range is sized to the target. Bounds keep it readable.
const X_CAP = 11, Y_CAP = 7;   // largest axis extent
const X_MIN = 5, Y_MIN = 4;    // smallest axis extent (so tiny targets still get a plane)

/** Which signs the target coordinates take. */
type SignMode = 'pos' | 'neg' | 'mixed';

function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}
function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/** Both coords non-zero, signs per `mode`. Magnitudes span a wide range so the
 *  grid scales question to question. */
function coordFor(mode: SignMode): { tx: number; ty: number } {
  const ax = randInt(1, 10);
  const ay = randInt(1, 6);
  if (mode === 'pos') return { tx: ax, ty: ay };
  if (mode === 'neg') return { tx: -ax, ty: -ay };
  // mixed — Q2 (−,+) or Q4 (+,−)
  return Math.random() < 0.5 ? { tx: ax, ty: -ay } : { tx: -ax, ty: ay };
}

/** Negatives raise the challenge: all-positive is easy, all-negative harder,
 *  mixed signs (opposite directions per axis) is hardest. */
const DIFFICULTY: Record<SignMode, DifficultyLevel> = {
  pos: DifficultyLevel.EASY,
  neg: DifficultyLevel.MEDIUM,
  mixed: DifficultyLevel.HARD
};

function toRound(mode: SignMode, idx: number): GridRound {
  const { tx, ty } = coordFor(mode);
  // Grid extent = enough to hold the target with a unit of margin.
  const maxX = clamp(Math.abs(tx) + 1, X_MIN, X_CAP);
  const maxY = clamp(Math.abs(ty) + 1, Y_MIN, Y_CAP);
  return { id: `grid-${idx}-${tx}_${ty}`, tx, ty, maxX, maxY, difficulty: DIFFICULTY[mode] };
}

/**
 * Build a session's rounds. The first turn is a small all-positive target (a
 * gentle intro), then the rest are a balanced, shuffled mix of all-positive,
 * all-negative and mixed-sign targets — so both the sign and the grid scale
 * vary randomly turn to turn.
 */
export function buildTreasureGridRounds(count: number): GridRound[] {
  const n = Math.max(1, count);
  const modes: SignMode[] = ['pos'];
  const cycle: SignMode[] = ['neg', 'mixed', 'pos'];
  for (let i = 0; modes.length < n; i++) modes.push(cycle[i % cycle.length]);
  const rest = shuffle(modes.slice(1)).slice(0, n - 1);
  const ordered: SignMode[] = [modes[0], ...rest];
  return ordered.map((m, idx) => toRound(m, idx));
}
